import { Injectable } from '@angular/core'
import { ActivatedRouteSnapshot, CanActivate, CanLoad, Router, RouterStateSnapshot } from '@angular/router'
import { Services } from 'src/app/shared/constants/services'
import { StorageKeys } from 'src/app/shared/constants/storage-keys'
import { ServiceDetail } from 'src/app/shared/models/serviceDetail'

import { CoreModule } from '../../core.module'
import { AuthService } from '../auth/auth.service'
import { LocalStorageService } from '../storage/local-storage.service'

@Injectable({
  providedIn: CoreModule
})
export class AuthGuardService implements CanLoad, CanActivate {
  canLoad(): boolean {
    if (!this.authService.isLoggedIn) return false
    // Dex users flagged for a forced password reset are blocked from the app
    // until they set a new password (first login after create, or post-edit).
    if (this.storage.get(StorageKeys.mustResetPassword)) {
      this.router.navigate(['force-reset'])
      return false
    }
    return true
  }

  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    if (this.authService.isLoggedIn) {
      if (this.storage.get(StorageKeys.mustResetPassword)) {
        this.router.navigate(['force-reset'])
        return false
      }
      if (!this.checkPermissions(state)) {
        this.router.navigate(['']) // canActivate=>false results in blank screen/dead link
        return false
      }

      return true
    }
    return false
  }

  checkPermissions(state: RouterStateSnapshot): boolean {
    const route: string = state.url
    const userPermissionAvailed = this.storage.get(StorageKeys.userPermissions)
    // Checked first so no broader match below can shadow it. The API spec these pages render is served
    // behind ORG_SET, so the route must require the same permission the backend does.
    if (route.includes('/i-api') || route.endsWith('/api')) {
      return !!userPermissionAvailed['orgSettingsAccess']
    }
    if (
      route.includes('client') ||
      route.includes('learning') ||
      route.includes('search') ||
      route.includes('category') ||
      route.includes('keywords')
    ) {
      return true
    } else if (route.includes('user')) {
      if (route.includes('user-management')) {
        return !!userPermissionAvailed['canManageUsers']
      } else if (route.includes('create')) {
        return !!userPermissionAvailed['canCreateUsers']
      }
      return false
    } else if (route.includes('create-templates') || route.includes('template-list')) {
      return !!userPermissionAvailed['templateAccess']
    } else if (route.includes('global-search-replace')) {
      return !!userPermissionAvailed['globalSearchReplace']
    } else if (route.includes('self-service')) {
      return !!userPermissionAvailed['selfServiceAccess']
    } else if (route.includes('miscellaneous')) {
      return !!userPermissionAvailed['miscellaneousAccess']
    } else if (route.includes('document-library')) {
      return !!userPermissionAvailed['docLibraryAccess']
    } else if (route.includes('settings/ai-studio')) {
      return !!userPermissionAvailed['orgSettingsAccess']
    } else if (route.includes('custom-labels')) {
      return !!userPermissionAvailed['translationAccess']
    } else if (route.includes('settings/org')) {
      return !!userPermissionAvailed['orgSettingsAccess']
    } else if (route.includes('channels')) {
      return !!userPermissionAvailed['canManageUsers']
    } else if (route.includes('search-configuration')) {
      return !!userPermissionAvailed['hasSearchConfigAccess']
    } else if (route.includes('attribute-sets')) {
      return !!userPermissionAvailed['attributeSetManage']
    } else if (route.includes('dt-session')) {
      return !!userPermissionAvailed['dtSessionRead']
    } else if (route.includes('audit-log')) {
      return !!userPermissionAvailed['dtSessionRead']
    } else {
      const servicesAvailed = (this.storage.get(StorageKeys.serviceDetails) || []) as ServiceDetail[]
      const r = route.split('/')[1]
      const service = Services.find(service => {
        return service.route.find(serviceRoute => serviceRoute.url?.includes(r))
      })

      const permission = servicesAvailed.find(s => {
        return service?.serviceCode == s?.service?.code
      })

      if (route.includes('lms')) {
        if (route.includes('assignment')) {
          return !!permission?.assign
        }
        return true
      }

      return !!permission?.write
    }
  }

  constructor(
    private readonly storage: LocalStorageService,
    private readonly authService: AuthService,
    public readonly router: Router
  ) {}
}
